import type { BreakpointKey } from '../constants/breakpoint';
import { RESIZER_URL, BREAKPOINTS } from '../constants';
import { dev } from '$app/environment';

const imageProcessingKeys = [
  'width',
  'height',
  'dpr',
  'fit',
  'gravity',
  'quality',
  'format',
  'sharpen',
  'blur'
] as const;
export type ImageProcessingKey = (typeof imageProcessingKeys)[number];
type DefaultWithBreakpointKey = 'DEFAULT' | BreakpointKey;

type ImageOptions = {
  width?: number;
  height?: number;
  dpr?: number;
  fit?: string;
  gravity?: string;
  quality?: number;
  format?: string;
  sharpen?: number;
  blur?: number;
};

export function resizeUrl(url: string, opts: ImageOptions = {}) {
  if (!opts.width && !opts.height) {
    throw new Error('Must provide width or height to resizeImage');
  }

  const query = new URLSearchParams();

  // Add the target image to the query
  try {
    const { pathname } = new URL(url);
    query.append('image', pathname);
  } catch (_) {
    // Most likely a relative URL
    query.append('image', url);
  }

  // Apply options
  imageProcessingKeys.forEach((key: ImageProcessingKey) => {
    if (key in opts && opts[key] !== undefined) {
      query.append(key, opts[key] as string);
    }
  });

  // Always default format to auto (webp for now).
  if (!opts.format) {
    query.append('format', 'webp');
  }

  return `${RESIZER_URL}?${query.toString()}`;
}

// Create these regexes only once at the top level.
const matchers: Record<DefaultWithBreakpointKey, any> = {} as any;
const matchersJit: Record<DefaultWithBreakpointKey, any> = {} as any;
(['DEFAULT', ...Object.keys(BREAKPOINTS)] as DefaultWithBreakpointKey[]).forEach(bp => {
  const bpKey = bp === 'DEFAULT' ? '' : bp + ':';
  matchers[bp] = {};
  matchersJit[bp] = {};
  ['w', 'h'].forEach(sizeKey => {
    matchers[bp][sizeKey] = new RegExp(bpKey + sizeKey + '-([0-9.]+)');
    matchersJit[bp][sizeKey] = new RegExp(bpKey + sizeKey + '-\\[([0-9]+)\\px]');
  });
});

function extractSize(classes: string, key: 'w' | 'h', bp: DefaultWithBreakpointKey) {
  let match = classes.match(matchers[bp][key]);
  if (match) {
    return parseFloat(match[1]) * 4;
  }
  match = classes.match(matchersJit[bp][key]);
  return match ? parseInt(match[1], 10) : undefined;
}

function extractSizes(size: string) {
  const sizes: Record<DefaultWithBreakpointKey, { width?: number; height?: number }> = {} as any;
  (['DEFAULT', ...Object.keys(BREAKPOINTS)] as DefaultWithBreakpointKey[]).forEach(bp => {
    const width = extractSize(size, 'w', bp);
    const height = extractSize(size, 'h', bp);
    if (width !== undefined || height !== undefined) {
      sizes[bp] = { width, height };
    }
  });
  return sizes;
}

function urlEndsWith(url: string, ends: string[]): boolean {
  return ends.some(element => {
    return url.endsWith(element);
  });
}

export function resizeImage(url: string, size: string, opts?: any) {
  if (
    (import.meta.env.VITEST !== 'true' && dev) ||
    size.length === 0 ||
    !url ||
    url.length === 0 ||
    typeof url !== 'string'
  ) {
    return {};
  }

  const allowed = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.avif'];

  const isAllowed = urlEndsWith(url, allowed);

  const sizes = extractSizes(size);
  if (Object.keys(sizes).length === 0) {
    return {};
  }

  // If the width is not defined we can't build the responsive `srcset`, so instead we just use the image with the biggest height.
  if (isAllowed && Object.values(sizes).every(({ width }) => width === undefined)) {
    const biggest = Object.values(sizes).reduce(
      (acc, v) => {
        if (v.height! > acc.height!) {
          return v;
        }
        return acc;
      },
      { width: -Infinity, height: -Infinity }
    );
    return {
      src: resizeUrl(url, { ...opts, ...biggest }),
      attributes: biggest
    };
  }

  const srcsetItems = [];
  const sizesItems = [];
  const resized: Record<DefaultWithBreakpointKey, string> = {} as any;

  if (isAllowed) {
    for (const [bp, { width, height }] of Object.entries(sizes)) {
      resized[bp as DefaultWithBreakpointKey] = resizeUrl(url, { ...opts, width, height });

      // Generate `srcset` and `sizes` attributes.
      srcsetItems.push(`${resized[bp as DefaultWithBreakpointKey]} ${width}w`);
      if (bp === 'DEFAULT') {
        sizesItems.push(width + 'px');
      } else {
        sizesItems.push(`(min-width: ${BREAKPOINTS[bp as BreakpointKey]}px) ${width}px`);
      }
    }
  }

  // Get smallest size to use for the `width` and `height` attributes.
  const smallestSize = Object.values(sizes).reduce(
    (acc, v) => {
      if ((v.width !== undefined && v.width < acc.width!) || (v.height !== undefined && v.height < acc.height!)) {
        return v;
      }
      return acc;
    },
    { width: Infinity, height: Infinity }
  );

  return {
    srcResized: resized,
    src: 'DEFAULT' in resized ? resized['DEFAULT'] : url,
    srcset: srcsetItems.length > 0 ? srcsetItems.join(', ') : undefined,
    sizes: sizesItems.length > 0 ? sizesItems.reverse().join(', ') : undefined,
    attributes: smallestSize
  };
}
